import Swiper from 'swiper';
import {
    Navigation, Pagination, A11y, EffectFade, FreeMode, Mousewheel
}             from 'swiper/modules';

/**
 * Create a slider using Swiper library
 *
 * @param {HTMLElement} el - The element that contains the slider
 * @param {Object} config - Configuration options for the slider
 * @returns {Swiper|false} - The created Swiper instance
 */
function sliders(el, config = {}) {
    // Check if the element exists
    if (!el) {
        return false;
    }

    // Get the required elements for the slider
    const slider     = el.querySelector('.swiper');
    const next       = el.querySelector('.swiper-button-next');
    const prev       = el.querySelector('.swiper-button-prev');
    const pagination = el.querySelector('.swiper-pagination');

    // Set the default settings for the slider
    const settings = {
        modules               : [Navigation, Pagination, A11y, EffectFade, FreeMode, Mousewheel],
        loop                  : false,
        slidesPerView         : 'auto',
        spaceBetween          : 0,
        watchSlidesVisibility : true,
        watchOverflow         : true,
        threshold             : 10,
        observer              : true,
        navigation            : {
            nextEl : next,
            prevEl : prev
        },
        a11y : {
            prevSlideMessage        : 'Предыдущий слайд',
            nextSlideMessage        : 'Следующий слайд',
            paginationBulletMessage : 'Перейти к {{index}}',
            firstSlideMessage       : 'Первый слайд'
        },
        pagination : {
            el        : pagination,
            type      : 'bullets',
            clickable : true
        },
        ...config
    };

    // Create and return a new Swiper instance
    return new Swiper(slider, settings);
}

/**
 * Create a responsive slider that initializes/destroys based on breakpoint
 *
 * @param {HTMLElement} el - The element that contains the slider
 * @param {Object} config - Configuration options for the slider
 * @param {number} [config.breakpoint=681] - Breakpoint in pixels (slider disabled above this width)
 * @param {Object} [config.settings={}] - Swiper settings to merge with defaults
 * @returns {Object|false} - Object with swiper instance and destroy method, or false if element not found
 */
function slidersResize(el, config = {}) {
    if (!el) {
        return false;
    }

    // Extract breakpoint from config, default to 681px
    const breakpointValue = config.breakpoint ?? 681;
    const userSettings    = config.settings ?? {};

    // Get the required elements for the slider
    const slider     = el.querySelector('.swiper');
    const pagination = el.querySelector('.swiper-pagination');

    if (!slider) {
        console.warn('Slider element (.swiper) not found');
        return false;
    }

    // Create media query with custom breakpoint
    const breakpoint = window.matchMedia(`(min-width: ${breakpointValue}px)`);

    let swiper;
    let mediaQueryListener;

    // Default settings for the slider
    const settings = {
        loop                  : false,
        slidesPerView         : 1,
        spaceBetween          : 16,
        preloadImages         : false,
        watchSlidesVisibility : true,
        watchOverflow         : true,
        threshold             : 0,
        observer              : true,
        pagination            : {
            el             : pagination,
            type           : 'bullets',
            clickable      : true,
            dynamicBullets : false
        },
        ...userSettings
    };

    /**
     * Initialize the Swiper slider
     */
    const enableSlider = () => {
        if (!swiper) {
            swiper = new Swiper(slider, settings);
        }
    };

    /**
     * Destroy the Swiper slider
     */
    const disableSlider = () => {
        if (swiper) {
            swiper.destroy(true, true);
            swiper = undefined;
        }
    };

    /**
     * Check breakpoint and enable/disable slider accordingly
     */
    const breakpointChecker = () => {
        if (breakpoint.matches) {
            // Above breakpoint - disable slider
            disableSlider();
        } else {
            // Below breakpoint - enable slider
            enableSlider();
        }
    };

    /**
     * Cleanup function to remove event listeners
     */
    const cleanup = () => {
        disableSlider();
        if (mediaQueryListener) {
            try {
                breakpoint.removeEventListener('change', mediaQueryListener);
            } catch (e) {
                // Safari <14
                breakpoint.removeListener(mediaQueryListener);
            }
        }
    };

    // Set up event listener with fallback for older browsers
    mediaQueryListener = breakpointChecker;
    try {
        breakpoint.addEventListener('change', mediaQueryListener);
    } catch (e) {
        // Safari <14
        breakpoint.addListener(mediaQueryListener);
    }

    // Initial check
    breakpointChecker();

    // Return object with cleanup method for manual destruction
    return {
        swiper : () => swiper,
        cleanup
    };
}

function sliderHomes(el, config = {}) {
    if (!el) return false;

    const slider     = el.querySelector('.swiper');
    const next       = el.querySelector('.swiper-button-next');
    const prev       = el.querySelector('.swiper-button-prev');
    const pagination = el.querySelector('.swiper-pagination');
    const video      = document.querySelector('video');

    const settings = {
        modules               : [Navigation, Pagination, A11y, EffectFade, FreeMode, Mousewheel],
        loop                  : false,
        slidesPerView         : 'auto',
        spaceBetween          : 0,
        watchSlidesVisibility : true,
        watchOverflow         : true,
        threshold             : 10,
        observer              : true,
        navigation            : {
            nextEl : next,
            prevEl : prev
        },
        a11y : {
            prevSlideMessage        : 'Предыдущий слайд',
            nextSlideMessage        : 'Следующий слайд',
            paginationBulletMessage : 'Перейти к {{index}}',
            firstSlideMessage       : 'Первый слайд'
        },
        pagination : {
            el        : pagination,
            type      : 'bullets',
            clickable : true
        },
        on : {
            init() {
                const { hash } = window.location;

                if (hash && hash.startsWith('#slide-')) {
                    const lastSlideIndex = this.slides.length - 1;

                    setTimeout(() => {
                        this.slideTo(lastSlideIndex, 0);
                    }, 100);

                    if (video) {
                        video.style.opacity = '0';
                    }
                }
            },

            slideChange() {
                this.allowSlideNext = !this.isEnd;

                if (video) {
                    if (this.isEnd) {
                        video.style.opacity = '0';
                    } else {
                        video.style.opacity = '1';
                    }
                }
            }
        },
        ...config
    };

    const swiperInstance = new Swiper(slider, settings);
    el.swiperInstance    = swiperInstance;
    const buttons        = document.querySelectorAll(`.btn-slide[data-target="${el.dataset.slider}"]`);

    buttons.forEach((btn) => {
        const updateButtonVisibility = () => {
            if (swiperInstance.activeIndex === swiperInstance.slides.length - 1) {
                btn.style.display = 'none';
            } else {
                btn.style.display = '';
            }
        };
        updateButtonVisibility();
        btn.addEventListener('click', () => {
            swiperInstance.slideTo(swiperInstance.slides.length - 1);
        });

        swiperInstance.on('slideChange', updateButtonVisibility);
    });

    return swiperInstance;
}

export default {
    sliders,
    slidersResize,
    sliderHomes
};
